import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import EditIcon from "@mui/icons-material/Edit";
import { Box, Grid, Paper, Typography, Card, CardContent, Tooltip, IconButton, Tabs, Tab, Stack } from '@mui/material';
import AttachmentsSection from './Attachments';
import ShopVisitField from './ShopVisitField';
import FundBalanceCard from './FundBalanceCard';
import SSkeletonLoader from '../../../shared_elements/skeleton_loader/SSkeletonLoader';
import { PageLoader } from '../../../shared_elements';
import AsOfDateCard from './AsOfDateCard';
import { trackActivity } from '../../../utils/mixpanel';
import ApprovalHeader from './ShopvisitApprovalHeader';
import WorkScopeTable from './Workscope';
import ClaimCard from './ClaimCard';
import LLP from './LLP';
import { assetType,  fieldDateFormat } from '../../../constants';
import { hasLlpEvent } from '../utils';
import { getLllpListApi, getClaimListApi } from '../apiServices';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { useShopVisitActions } from '../hooks/useApiHook';

const ShopVisitCard = ({ assetData, shopVisit, isLoading, fundBalanceLoader, dropdownLoader, llpModuleData, getShopVisit, handleUpload, getShopVisitFundBalance }) => {
  const params = useParams();
  const { saveShopVisit } = useShopVisitActions(params);
  const [tabIndex, setTabIndex] = useState(0)
  const [claimLoading, setClaimLoading] = useState(false)
  const [claimInfo, setClaimInfo] = useState([])
  const [claimListLoader, setClaimListLoader] = useState({ skeletonLoader: false, pageLoader: false })
  const [llpList, setLlpList] = useState([])
  const [llpListLoader, setLlpListLoader] = useState({ skeletonLoader: false, pageLoader: false })
  const totalSvCost = llpList.reduce((sum, item) => sum + (Number(item?.sv_amount) || 0), 0)
  const [asOfDate, setAsOfDate] = useState(null);
  const [editMode, setEditMode] = useState(false)
  const updatedAsset = {
    asset_type: assetData?.asset_type === 1 && shopVisit?.major_assembly && Object.keys(shopVisit?.major_assembly).length > 0 ? assetType[shopVisit?.major_assembly?.asset_type] : assetType[assetData?.asset_type],
    asset_slug: assetData?.asset_type === 1 && shopVisit?.major_assembly && Object.keys(shopVisit?.major_assembly).length > 0 ? shopVisit?.major_assembly?.asset_slug : assetData?.slug,
  }
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Shop Visit View`, page_type: page_type, })
  }
  const onTabChange = (e, val) => setTabIndex(val);
  const isLlpEvent = hasLlpEvent(shopVisit);

  const updateShopVisitLlpCost = (shopVisitData, newTotalCost) => {
    if (!shopVisitData?.slug || !Array.isArray(shopVisitData?.events) || !shopVisitData.events.some((ev) => ev?.maintenance_program?.is_llp_event === true)) return;
    const payload = {
      ...shopVisitData,
      csn: Number(shopVisitData?.csn) || 0,
      tsn: Number(shopVisitData?.tsn) || 0,
      cost: Number(shopVisitData?.cost) || 0,
      events: shopVisitData.events.map((ev) => ({
        id: ev?.id,
        maintenance_program_id: ev?.maintenance_program?.id,
        cost: ev?.maintenance_program?.is_llp_event === true ? newTotalCost : ev?.cost,
        modules: ev?.modules || [],
      })),
    };
    delete payload.modules;
    delete payload.maintenance_check;
    delete payload.maintenance_check_id;
    return saveShopVisit(assetData, shopVisitData, payload, () => {}, true).then((response) => {
      if (response) { getShopVisit(); }
      return response;
    });
  }

  const getLlpList = (shopVisitData, query = {}, loaderType, syncShopVisitCost = false) => {
    if (shopVisitData?.slug && hasLlpEvent(shopVisitData)) {
      setLlpListLoader({ [loaderType]: true });
      return globalGetService(getLllpListApi(shopVisitData.slug, query)).then((response) => {
        let result = true;
        if (checkApiStatus(response)) {
          const list = response?.data?.data || [];
          setLlpList(list);
          if (syncShopVisitCost) {
            const total = list.reduce((sum, item) => sum + (Number(item?.sv_amount) || 0), 0);
            result = updateShopVisitLlpCost(shopVisitData, total);
          }
        }
        setLlpListLoader({ [loaderType]: false });
        return result;
      });
    }
    return Promise.resolve(true);
  }

  useEffect(() => {
    getLlpList(shopVisit, {}, 'skeletonLoader');
  }, [shopVisit?.slug, isLlpEvent]);

  const getClaimList = (loaderType = 'pageLoader') => {
    if (!shopVisit?.id) return Promise.resolve();
    setClaimListLoader((prev) => ({ ...prev, [loaderType]: true }));
    return globalGetService(getClaimListApi(params, updatedAsset, { shop_visit: shopVisit?.id }), {}).then((response) => {
      if (checkApiStatus(response)) { setClaimInfo(response?.data?.data); }
    }).finally(() => {
      setClaimListLoader((prev) => ({ ...prev, [loaderType]: false }));
    });
  };
  useEffect(() => {
    if (shopVisit?.id && shopVisit?.status?.value === 3) { getClaimList('skeletonLoader'); }
  }, [shopVisit?.id, shopVisit?.status?.value]);
  const onFieldChange = (date) => { const m = moment(date); setAsOfDate(m); }
  const isInitialLoad = !shopVisit?.id;
  const showPageSkeleton = isInitialLoad && (isLoading?.pageLoader || fundBalanceLoader || claimLoading);
  const showOperationLoader = !isInitialLoad && (isLoading?.pageLoader || fundBalanceLoader || claimLoading);
  return (
    <>
      {showOperationLoader ? <PageLoader /> : null}
      <Grid >
        <Box className="shop-visit-card" sx={{ p: 1, height: "calc(100vh - 215px)", overflowY: "auto", overflowX: "hidden" }}>
        {showPageSkeleton ? <SSkeletonLoader /> : null}
        <Box sx={{ display: showPageSkeleton ? 'none' : 'block' }}>
        {shopVisit?.status && <Paper
                  elevation={2}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2
                  }}
                >
                  <ApprovalHeader isLoading={isLoading} getShopVisit={getShopVisit} shopVisit={shopVisit} handleUpload={handleUpload} handleItemClicked={handleItemClicked} />
                </Paper>
                }
          <Grid container spacing={1} sx={{ display: 'flex', flexDirection: "row", justifyContent: 'space-between' }}>
          
            <Grid size={{ xs: 12, md: 8.8 }}>
              <Paper sx={{ p: 2, }}  >
                <ShopVisitField assetData={assetData} llpModuleData={llpModuleData} llpList={llpList} dropdownLoader={dropdownLoader} shopVisit={shopVisit} modeType="view" totalSvCost={totalSvCost} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 3.2 }}>
              <Card sx={{ mb: 2 }}>
                <CardContent sx={{
                  p: 1,
                  "&:last-child": {
                    pb: 2
                  }
                }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px"}}>
                    <Stack direction="row" alignItems="center" spacing={editMode ? 1.5 : 0.5}>
                      <Typography variant="subtitle2" component="span" sx={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                        Fund As of
                      </Typography>
                      {!editMode ? <Typography component="span" sx={{ fontSize: 14 }}>{shopVisit?.as_of_date ? moment(shopVisit?.as_of_date).format(fieldDateFormat) : "--/--/--"}</Typography> : <AsOfDateCard shopVisit={shopVisit} asOfDate={asOfDate} onFieldChange={onFieldChange}  onToggleMode={() => setEditMode(false)} getResponseBack={getShopVisit} />}
                      {!editMode && !(Array.isArray(claimInfo?.list) && claimInfo.list.some((c) => [5, 6].includes(c?.payout_status?.value))) && <Tooltip title="Edit Date">
                        <IconButton size="small" color="primary" onClick={() => { setEditMode(true); setAsOfDate(moment(shopVisit?.as_of_date)) }} > <EditIcon fontSize="small" /> </IconButton>
                      </Tooltip>}
                    </Stack>
                  </Box>
                  <FundBalanceCard shopVisit={shopVisit} claimInfo={claimInfo} />
                </CardContent>
              </Card>
              <AttachmentsSection attachmentData={shopVisit?.attachments} />
            </Grid>

          </Grid>
          <Box sx={{mt:1}}>
          {((assetData?.asset_type === 1 && shopVisit?.major_assembly?.asset_type === 2) || assetData?.asset_type === 2) && !isInitialLoad ?
            <Paper sx={{mb:1}}>
              <Tabs value={tabIndex} onChange={onTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }} >
                {shopVisit?.status?.value === 3 ? <Tab label="Claim" /> : null}
                {isLlpEvent ? <Tab label="LLP" /> : null}
                <Tab label="Workscope" />
              </Tabs>
            </Paper> :
            <>
              {updatedAsset?.asset_type && updatedAsset?.asset_slug && shopVisit?.id && shopVisit?.status?.value === 3 ? <ClaimCard shopVisit={shopVisit} updatedAsset={updatedAsset} llpModuleData={llpModuleData} llpList={llpList} getShopVisit={getShopVisit} getShopVisitFundBalance={getShopVisitFundBalance} onLoadingChange={setClaimLoading} claimInfo={claimInfo} claimListLoader={claimListLoader} getClaimList={getClaimList} /> : null}</>
          }
          {tabIndex === 0 && shopVisit?.status?.value === 3 && shopVisit?.id && updatedAsset?.asset_type && updatedAsset?.asset_slug && ((assetData?.asset_type === 1 && shopVisit?.major_assembly?.asset_type === 2) || assetData?.asset_type === 2) ? <ClaimCard shopVisit={shopVisit} updatedAsset={updatedAsset} llpModuleData={llpModuleData} llpList={llpList} onTabChange={onTabChange} getShopVisit={getShopVisit} getShopVisitFundBalance={getShopVisitFundBalance} onLoadingChange={setClaimLoading} claimInfo={claimInfo} claimListLoader={claimListLoader} getClaimList={getClaimList} /> : null}
          {updatedAsset?.asset_type === "engine" && !isInitialLoad ?
            <> {(isLlpEvent && shopVisit?.status?.value === 3 && tabIndex === 1) || (isLlpEvent && shopVisit?.status?.value !== 3 && tabIndex === 0) ? <LLP shopVisit={shopVisit} claimInfo={claimInfo} getShopVisitFundBalance={getShopVisitFundBalance} llpList={llpList} llpListLoader={llpListLoader} getLlpList={getLlpList} updateShopVisitLlpCost={updateShopVisitLlpCost} /> : null}
              {(isLlpEvent && shopVisit?.status?.value === 3 && tabIndex === 2) || (isLlpEvent && shopVisit?.status?.value !== 3 && tabIndex === 1) || (!isLlpEvent && shopVisit?.status?.value !== 3 && tabIndex === 0) || (!isLlpEvent && shopVisit?.status?.value === 3 && tabIndex === 1) ?
                <>
                  <WorkScopeTable shopVisit={shopVisit} />
                </> : null
              }
            </> : null}
          </Box>

        </Box>
        </Box>
      </Grid>
    </>

  )
}

export default ShopVisitCard;